import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { FormSection } from '../schema/types';

export interface FilterRolesRequest {
  availableRoles: FormSection[];
  currentSelectedRoles: string[];
}

@customElement('jupiter-filter-roles-dialog')
export class JupiterFilterRolesDialog extends LitElement {
  @property({ type: Boolean, reflect: true }) open = false;
  @property({ type: Array }) availableRoles: FormSection[] = [];
  @property({ type: Array }) selectedRoleIds: string[] = [];

  @state() private _tempSelectedRoles = new Set<string>();
  @state() private _searchQuery = '';
  @state() private _filteredRoles: FormSection[] = [];

  static styles = css`
    :host {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      z-index: 10000;
      display: none;
      align-items: center;
      justify-content: center;
      background: rgba(0, 0, 0, 0.5);
      font-family: var(--jupiter-font-family, system-ui, -apple-system, sans-serif);
    }

    :host([open]) {
      display: flex;
    }

    .dialog {
      background: var(--jupiter-background, #fff);
      border-radius: 8px;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
      
      width: 90vw;
      max-height: 80vh;
      display: flex;
      flex-direction: column;
      overflow: hidden;
    }

    .dialog-header {
      padding: 20px 24px 16px;
      border-bottom: 1px solid var(--jupiter-border-color, #ddd);
      display: flex;
      align-items: center;
      gap: 12px;
    }

    .dialog-title {
      font-size: 18px;
      font-weight: 600;
      margin: 0;
      color: var(--jupiter-text-primary, #333);
      flex: 1;
    }

    .filter-icon {
      width: 20px;
      height: 20px;
      fill: var(--jupiter-primary-color, #1976d2);
    }

    .close-button {
      background: none;
      border: none;
      font-size: 24px;
      cursor: pointer;
      padding: 4px;
      color: var(--jupiter-text-secondary, #666);
      border-radius: 4px;
      transition: background-color 0.2s ease;
    }

    .close-button:hover {
      background: var(--jupiter-hover-background, #f5f5f5);
    }

    .dialog-content {
      flex: 1;
      overflow-y: auto;
      padding: 20px 24px;
    }

    .description {
      color: var(--jupiter-text-secondary, #666);
      font-size: 14px;
      margin: 0 0 20px 0;
      line-height: 1.5;
    }

    .selection-controls {
      display: flex;
      gap: 12px;
      margin-bottom: 20px;
      flex-wrap: wrap;
    }

    .search-container {
      margin-bottom: 16px;
      position: relative;
    }

    .search-input {
      width: 100%;
      padding: 10px 40px 10px 12px;
      border: 1px solid var(--jupiter-border-color, #ddd);
      border-radius: 4px;
      font-size: 14px;
      font-family: inherit;
      background: var(--jupiter-background, #fff);
      color: var(--jupiter-text-primary, #333);
      transition: border-color 0.2s ease;
    }

    .search-input:focus {
      outline: none;
      border-color: var(--jupiter-primary-color, #1976d2);
      box-shadow: 0 0 0 2px rgba(25, 118, 210, 0.1);
    }

    .search-input::placeholder {
      color: var(--jupiter-text-secondary, #666);
    }

    .search-icon {
      position: absolute;
      right: 12px;
      top: 50%;
      transform: translateY(-50%);
      width: 16px;
      height: 16px;
      fill: var(--jupiter-text-secondary, #666);
      pointer-events: none;
    }

    .clear-search {
      position: absolute;
      right: 12px;
      top: 50%;
      transform: translateY(-50%);
      background: none;
      border: none;
      cursor: pointer;
      padding: 2px;
      border-radius: 2px;
      color: var(--jupiter-text-secondary, #666);
      font-size: 16px;
      line-height: 1;
      transition: color 0.2s ease;
    }

    .clear-search:hover {
      color: var(--jupiter-text-primary, #333);
    }

    .search-results-info {
      font-size: 12px;
      color: var(--jupiter-text-secondary, #666);
      margin-bottom: 8px;
      text-align: center;
    }

    .no-results {
      text-align: center;
      padding: 40px 20px;
      color: var(--jupiter-text-secondary, #666);
      font-style: italic;
    }

    mark {
      background: var(--jupiter-primary-color, #1976d2);
      color: white;
      padding: 1px 2px;
      border-radius: 2px;
      font-weight: 500;
    }

    .selection-control {
      background: none;
      border: 1px solid var(--jupiter-primary-color, #1976d2);
      color: var(--jupiter-primary-color, #1976d2);
      padding: 6px 12px;
      border-radius: 4px;
      font-size: 12px;
      cursor: pointer;
      transition: all 0.2s ease;
    }

    .selection-control:hover {
      background: var(--jupiter-primary-color, #1976d2);
      color: white;
    }

    .roles-list {
      display: flex;
      flex-direction: column;
      gap: 8px;
      max-height: 300px;
      overflow-y: auto;
      border: 1px solid var(--jupiter-border-color, #ddd);
      border-radius: 4px;
      padding: 12px;
    }

    .role-item {
      display: flex;
      align-items: flex-start;
      gap: 12px;
      padding: 8px;
      border-radius: 4px;
      transition: background-color 0.2s ease;
    }

    .role-item:hover {
      background: var(--jupiter-hover-background, #f5f5f5);
    }

    .role-checkbox {
      margin-top: 2px;
      cursor: pointer;
    }

    .role-info {
      flex: 1;
      min-width: 0;
    }

    .role-title {
      font-weight: 500;
      color: var(--jupiter-text-primary, #333);
      margin: 0 0 4px 0;
      word-wrap: break-word;
      line-height: 1.3;
    }

    .role-description {
      font-size: 13px;
      color: var(--jupiter-text-secondary, #666);
      margin: 0;
      line-height: 1.4;
      word-break: break-all; /* Allow URI breaking */
    }

    .role-description:last-child {
      margin-bottom: 4px;
    }

    .selected-count {
      font-size: 13px;
      color: var(--jupiter-text-secondary, #666);
      margin-top: 16px;
      text-align: center;
    }

    .dialog-actions {
      padding: 16px 24px;
      border-top: 1px solid var(--jupiter-border-color, #ddd);
      display: flex;
      gap: 12px;
      justify-content: flex-end;
      background: var(--jupiter-form-actions-background, #f8f9fa);
    }

    .dialog-actions button {
      padding: 10px 20px;
      border: none;
      border-radius: 4px;
      font-size: 14px;
      font-weight: 500;
      cursor: pointer;
      transition: background-color 0.2s ease;
    }

    .btn-primary {
      background: var(--jupiter-primary-color, #1976d2);
      color: white;
    }

    .btn-primary:hover:not(:disabled) {
      background: var(--jupiter-primary-color-dark, #1565c0);
    }

    .btn-secondary {
      background: transparent;
      color: var(--jupiter-text-secondary, #666);
      border: 1px solid var(--jupiter-border-color, #ddd);
    }

    .btn-secondary:hover:not(:disabled) {
      background: var(--jupiter-hover-background, #f5f5f5);
    }

    button:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  `;

  connectedCallback() {
    super.connectedCallback();
    this._initializeTempSelection();
  }

  updated(changedProperties: Map<string, any>) {
    if (changedProperties.has('selectedRoleIds') || changedProperties.has('open')) {
      this._initializeTempSelection();
    }
    if (changedProperties.has('availableRoles') || changedProperties.has('_searchQuery')) {
      this._updateFilteredRoles();
    }
  }

  private _updateFilteredRoles() {
    if (!this._searchQuery.trim()) {
      // Sort roles by title in ascending order
      this._filteredRoles = [...this.availableRoles].sort((a, b) => {
        return a.title.localeCompare(b.title);
      });
      return;
    }

    const query = this._searchQuery.toLowerCase().trim();
    const filtered = this.availableRoles.filter(role => {
      // Search in role title
      const titleMatch = role.title.toLowerCase().includes(query);
      
      // Search in role ID
      const idMatch = role.id.toLowerCase().includes(query);
      
      // Search in role description if available
      const descriptionMatch = role.description?.toLowerCase().includes(query) || false;
      
      // Search in roleURI if available (from metadata or if stored separately)
      // For now, we'll search in the title for URI-like patterns or check if role has URI-like structure
      const uriMatch = this._searchInRoleURI(role, query);
      
      return titleMatch || idMatch || descriptionMatch || uriMatch;
    });

    // Sort filtered results by title in ascending order
    this._filteredRoles = filtered.sort((a, b) => {
      return a.title.localeCompare(b.title);
    });
  }

  private _searchInRoleURI(role: FormSection, query: string): boolean {
    // If the role has metadata that contains roleURI
    if (role.metadata?.roleURI) {
      return role.metadata.roleURI.toLowerCase().includes(query);
    }
    
    // Look for URI patterns in title or ID
    const uriPattern = /https?:\/\/[^\s]+/i;
    const titleHasURI = uriPattern.test(role.title);
    const idHasURI = uriPattern.test(role.id);
    
    if (titleHasURI || idHasURI) {
      return role.title.toLowerCase().includes(query) || role.id.toLowerCase().includes(query);
    }
    
    // If role ID follows URI-like pattern (contains dots, slashes, etc.)
    if (role.id.includes('.') || role.id.includes('/') || role.id.includes(':')) {
      return role.id.toLowerCase().includes(query);
    }
    
    return false;
  }

  private _handleSearchInput(event: Event) {
    const input = event.target as HTMLInputElement;
    this._searchQuery = input.value;
  }

  private _clearSearch() {
    this._searchQuery = '';
    const searchInput = this.shadowRoot?.querySelector('.search-input') as HTMLInputElement;
    if (searchInput) {
      searchInput.value = '';
      searchInput.focus();
    }
  }

  private _highlightSearchTerm(text: string): string {
    if (!this._searchQuery.trim()) {
      return text;
    }

    const query = this._searchQuery.trim();
    const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    return text.replace(regex, '<mark>$1</mark>');
  }

  private _renderHighlightedText(text: string) {
    // For compatibility, just return the text without highlighting for now
    return text;
  }

  private _getSelectedFilteredCount(): number {
    return this._filteredRoles.filter(role => this._tempSelectedRoles.has(role.id)).length;
  }

  private _initializeTempSelection() {
    this._tempSelectedRoles = new Set(this.selectedRoleIds);
  }

  private _handleCheckboxChange(event: Event, roleId: string) {
    const checkbox = event.target as HTMLInputElement;
    const newSelection = new Set(this._tempSelectedRoles);
    
    if (checkbox.checked) {
      newSelection.add(roleId);
    } else {
      newSelection.delete(roleId);
    }
    
    this._tempSelectedRoles = newSelection;
    this.requestUpdate();
  }

  private _selectAll() {
    // Select all currently filtered roles
    const newSelection = new Set(this._tempSelectedRoles);
    this._filteredRoles.forEach(role => newSelection.add(role.id));
    this._tempSelectedRoles = newSelection;
    this.requestUpdate();
  }

  private _selectNone() {
    // Deselect all currently filtered roles
    const newSelection = new Set(this._tempSelectedRoles);
    this._filteredRoles.forEach(role => newSelection.delete(role.id));
    this._tempSelectedRoles = newSelection;
    this.requestUpdate();
  }

  private _selectAllGlobal() {
    // Select all available roles (ignore filter)
    this._tempSelectedRoles = new Set(this.availableRoles.map(role => role.id));
    this.requestUpdate();
  }

  private _selectNoneGlobal() {
    // Deselect all roles (ignore filter)
    this._tempSelectedRoles = new Set();
    this.requestUpdate();
  }

  private _resetToOriginal() {
    this._tempSelectedRoles = new Set(this.selectedRoleIds);
    this.requestUpdate();
  }

  private _handleCancel() {
    this._initializeTempSelection();
    this.dispatchEvent(new CustomEvent('dialog-cancel', {
      bubbles: true
    }));
  }

  private _handleApply() {
    const selectedRoles = Array.from(this._tempSelectedRoles);
    this.dispatchEvent(new CustomEvent('roles-filter-apply', {
      detail: { selectedRoleIds: selectedRoles },
      bubbles: true
    }));
  }

  private _handleBackdropClick(event: Event) {
    if (event.target === this) {
      this._handleCancel();
    }
  }

  render() {
    const selectedCount = this._tempSelectedRoles.size;
    const totalCount = this.availableRoles.length;
    const filteredCount = this._filteredRoles.length;
    const hasSearchQuery = this._searchQuery.trim().length > 0;

    return html`
      <div class="dialog" @click="${(e: Event) => e.stopPropagation()}">
        <!-- Dialog Header -->
        <div class="dialog-header">
          <svg class="filter-icon" viewBox="0 0 24 24">
            <path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z"/>
          </svg>
          <h2 class="dialog-title">Filter Roles</h2>
          <button class="close-button" @click="${this._handleCancel}">×</button>
        </div>

        <!-- Dialog Content -->
        <div class="dialog-content">
          <p class="description">
            Select the roles you want to display in the form. You can search and choose specific roles 
            to focus on or manage all ${totalCount} available roles.
          </p>

          <!-- Search Input -->
          <div class="search-container">
            <input
              type="text"
              class="search-input"
              placeholder="Search roles by name, ID, or URI..."
              .value="${this._searchQuery}"
              @input="${this._handleSearchInput}"
            />
            ${this._searchQuery ? html`
              <button class="clear-search" @click="${this._clearSearch}" title="Clear search">
                ×
              </button>
            ` : html`
              <svg class="search-icon" viewBox="0 0 24 24">
                <path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
              </svg>
            `}
          </div>

          <!-- Search Results Info -->
          ${hasSearchQuery ? html`
            <div class="search-results-info">
              Showing ${filteredCount} of ${totalCount} roles
              ${filteredCount !== totalCount ? html`matching "${this._searchQuery}"` : ''}
            </div>
          ` : ''}

          <!-- Selection Controls -->
          <div class="selection-controls">
            ${hasSearchQuery ? html`
              <button class="selection-control" @click="${this._selectAll}">
                Select Filtered (${filteredCount})
              </button>
              <button class="selection-control" @click="${this._selectNone}">
                Deselect Filtered
              </button>
              <button class="selection-control" @click="${this._selectAllGlobal}">
                Select All (${totalCount})
              </button>
              <button class="selection-control" @click="${this._selectNoneGlobal}">
                Deselect All
              </button>
            ` : html`
              <button class="selection-control" @click="${this._selectAll}">
                Select All
              </button>
              <button class="selection-control" @click="${this._selectNone}">
                Select None
              </button>
            `}
            <button class="selection-control" @click="${this._resetToOriginal}">
              Reset
            </button>
          </div>

          <!-- Roles List -->
          <div class="roles-list">
            ${this._filteredRoles.length === 0 ? html`
              <div class="no-results">
                ${hasSearchQuery ? html`
                  No roles found matching "${this._searchQuery}".<br>
                  Try a different search term or clear the search.
                ` : html`
                  No roles available.
                `}
              </div>
            ` : html`
              ${this._filteredRoles.map(role => {
                const isSelected = this._tempSelectedRoles.has(role.id);
                return html`
                  <div class="role-item">
                    <input
                      type="checkbox"
                      class="role-checkbox"
                      .checked="${isSelected}"
                      @change="${(e: Event) => this._handleCheckboxChange(e, role.id)}"
                    />
                    <div class="role-info">
                      <h4 class="role-title">${role.title}</h4>
                      ${role.metadata?.roleURI ? html`
                        <p class="role-description">URI: ${role.metadata.roleURI}</p>
                      ` : ''}
                    </div>
                  </div>
                `;
              })}
            `}
          </div>

          <div class="selected-count">
            ${selectedCount} of ${totalCount} roles selected
            ${hasSearchQuery && filteredCount !== totalCount ? html`
              <br><small>(${this._getSelectedFilteredCount()} of ${filteredCount} filtered roles selected)</small>
            ` : ''}
          </div>
        </div>

        <!-- Dialog Actions -->
        <div class="dialog-actions">
          <button class="btn-secondary" @click="${this._handleCancel}">
            Cancel
          </button>
          <button 
            class="btn-primary" 
            @click="${this._handleApply}"
            ?disabled="${selectedCount === 0}"
          >
            Apply Filter (${selectedCount})
          </button>
        </div>
      </div>
    `;
  }
}
